import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Card from '../components/UI/Card';
import StatCard from '../components/UI/StatCard';
import Badge from '../components/UI/Badge';
import Button from '../components/UI/Button';
import { accountAPI, notificationsAPI, positionsAPI } from '../services/api';
import { useSession } from '../lib/authClient';
import { PageTransition, Spinner } from '@/components/motion';

function AccountPortfolio() {
  const [accountInfo, setAccountInfo] = useState(null);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      accountAPI.getInfo('alpaca')
        .then((res) => { if (res.success) setAccountInfo(res.data); })
        .catch((err) => toast.error(`Failed to load account: ${err.message}`)),
      positionsAPI.getPortfolio()
        .then((res) => setPositions(res.data || []))
        .catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner />
      </div>
    );
  }

  if (!accountInfo) {
    return (
      <Card>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Could not load account info. Check your broker connection in Settings.</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
        <StatCard title="Equity" value={`$${parseFloat(accountInfo?.equity || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`} icon="💰" />
        <StatCard title="Cash" value={`$${parseFloat(accountInfo?.cash || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`} icon="🏦" />
        <StatCard title="Buying Power" value={`$${parseFloat(accountInfo?.buyingPower || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`} icon="⚡" />
        <StatCard title="Day Trade Count" value={accountInfo?.daytradeCount ?? '—'} icon="📊" />
      </div>

      <Card title="Account Details">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {accountInfo && Object.entries({
            'Status': accountInfo.status,
            'Portfolio Value': accountInfo.portfolioValue != null ? `$${parseFloat(accountInfo.portfolioValue).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '—',
            'Pattern Day Trader': accountInfo.patternDayTrader ? 'Yes' : 'No',
            'Trading Blocked': accountInfo.tradingBlocked ? 'Yes' : 'No',
            'Account Blocked': accountInfo.accountBlocked ? 'Yes' : 'No',
            'Day Trade Count': accountInfo.daytradeCount ?? '—',
          }).map(([key, value]) => (
            <div key={key} className="flex justify-between py-2 border-b border-border">
              <span className="text-sm text-muted-foreground">{key}</span>
              <span className="text-sm text-foreground font-medium">{value ?? '—'}</span>
            </div>
          ))}
        </div>
      </Card>

      <Card title="Positions">
        {positions.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full divide-y divide-white/8">
              <thead>
                <tr>
                  <th className="px-2 sm:px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">Symbol</th>
                  <th className="px-2 sm:px-4 py-2 text-right text-xs font-medium text-muted-foreground uppercase">Qty</th>
                  <th className="px-2 sm:px-4 py-2 text-right text-xs font-medium text-muted-foreground uppercase">Entry</th>
                  <th className="px-2 sm:px-4 py-2 text-right text-xs font-medium text-muted-foreground uppercase">Current</th>
                  <th className="px-2 sm:px-4 py-2 text-right text-xs font-medium text-muted-foreground uppercase">P&L</th>
                  <th className="px-2 sm:px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase hidden sm:table-cell">Strategy</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/6">
                {positions.map((p) => {
                  const qty = parseFloat(p.quantity);
                  const entry = parseFloat(p.entry_price);
                  const current = parseFloat(p.current_price || p.entry_price);
                  const pnl = (current - entry) * qty;
                  const pnlPct = entry > 0 ? ((current - entry) / entry) * 100 : 0;
                  const fmt = (v) => `$${parseFloat(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

                  return (
                    <tr key={p.id} className="hover:bg-white/[0.04] transition-colors">
                      <td className="px-2 sm:px-4 py-2 text-sm font-mono font-semibold text-foreground">{p.symbol}</td>
                      <td className="px-2 sm:px-4 py-2 text-sm text-muted-foreground text-right">{qty}</td>
                      <td className="px-2 sm:px-4 py-2 text-sm text-muted-foreground text-right">{fmt(entry)}</td>
                      <td className="px-2 sm:px-4 py-2 text-sm text-muted-foreground text-right">{fmt(current)}</td>
                      <td className={`px-2 sm:px-4 py-2 text-sm text-right font-medium ${pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {pnl >= 0 ? '+' : ''}{fmt(pnl)} ({pnlPct >= 0 ? '+' : ''}{pnlPct.toFixed(2)}%)
                      </td>
                      <td className="px-2 sm:px-4 py-2 text-sm hidden sm:table-cell">
                        {p.strategy_name ? (
                          <span className="text-muted-foreground">{p.strategy_name}</span>
                        ) : (
                          <Badge variant="neutral">Unlinked</Badge>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-muted-foreground">No open positions</p>
          </div>
        )}
      </Card>
    </div>
  );
}

function AccountBilling() {
  return (
    <Card title="Billing">
      <div className="text-center py-16">
        <div className="text-5xl mb-4">🧾</div>
        <p className="text-muted-foreground mb-2">Billing management coming soon</p>
        <p className="text-sm text-muted-foreground/70">Manage your subscription and payment methods here.</p>
      </div>
    </Card>
  );
}

const TYPE_STYLE = {
  error:   { variant: 'danger',  label: 'Error' },
  warning: { variant: 'warning', label: 'Warning' },
  info:    { variant: 'info',    label: 'Info' },
};

/** Derive a display badge from the notification's context (buy/sell/general). */
function getNotificationBadge(n) {
  const ctx = n.context || {};
  const side = ctx.side?.toLowerCase();

  if (side === 'buy') return { variant: 'success', label: 'BUY', borderClass: 'border-green-500/30' };
  if (side === 'sell') return { variant: 'danger', label: 'SELL', borderClass: 'border-red-500/30' };

  // Fall back to type-based styling
  const style = TYPE_STYLE[n.type] || TYPE_STYLE.info;
  return { ...style, borderClass: 'border-border' };
}

function AccountNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all' | 'unread'
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const params = {};
      if (filter === 'unread') params.unread = 'true';
      const res = await notificationsAPI.getAll(params);
      setNotifications(res.data ?? []);
    } catch (err) {
      toast.error(`Failed to load notifications: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  async function handleMarkAllRead() {
    try {
      await notificationsAPI.markAllRead();
      toast.success('All notifications marked as read');
      load();
    } catch (err) {
      toast.error(err.message);
    }
  }

  async function handleMarkRead(id) {
    try {
      await notificationsAPI.markRead(id);
      load();
    } catch (err) {
      toast.error(err.message);
    }
  }

  async function handleDeleteOne(id) {
    try {
      await notificationsAPI.deleteOne(id);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    } catch (err) {
      toast.error(err.message);
    }
  }

  async function handleDeleteOld() {
    try {
      const res = await notificationsAPI.deleteOld(14);
      const msg = res.message || 'Done';
      toast.success(msg);
      load();
    } catch (err) {
      toast.error(err.message);
    }
  }

  async function handleDeleteAll() {
    if (!window.confirm('Delete all notifications? This cannot be undone.')) return;
    try {
      await notificationsAPI.deleteOld(0); // 0 days = delete everything
      toast.success('All notifications deleted');
      load();
    } catch (err) {
      toast.error(err.message);
    }
  }

  function handleNotificationClick(n) {
    // Mark as read optimistically
    if (!n.read) {
      notificationsAPI.markRead(n.id).catch(() => {});
      setNotifications((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    }
    // Navigate to trading floor for the strategy, or the general trading floor
    if (n.strategy_id) {
      navigate(`/strategies/trading-floor/${n.strategy_id}`);
    } else {
      navigate('/strategies/trading-floor');
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        {['all', 'unread'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === f
                ? 'bg-cyan-600 text-white'
                : 'bg-card text-muted-foreground hover:bg-cyan-600/10'
            }`}
          >
            {f === 'all' ? 'All' : 'Unread'}
          </button>
        ))}
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="ghost" onClick={handleMarkAllRead}>Mark all read</Button>
          <Button size="sm" variant="ghost" onClick={handleDeleteOld}>Prune 14d+</Button>
          <Button size="sm" variant="ghost" onClick={handleDeleteAll} className="text-red-400/70 hover:text-red-400">Delete All</Button>
        </div>
      </div>

      {notifications.length === 0 ? (
        <Card>
          <div className="text-center py-16">
            <div className="text-5xl mb-4">🔔</div>
            <p className="text-muted-foreground">No notifications</p>
          </div>
        </Card>
      ) : (
        <div className="space-y-2">
          {notifications.map((n) => {
            const badge = getNotificationBadge(n);
            const ctx = n.context || {};

            return (
              <div
                key={n.id}
                onClick={() => handleNotificationClick(n)}
                className={`bg-card border rounded-xl px-5 py-4 flex items-start gap-4 transition-colors cursor-pointer hover:bg-white/[0.03] ${
                  n.read ? `${badge.borderClass} opacity-60` : badge.borderClass
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant={badge.variant}>{badge.label}</Badge>
                    <span className="text-sm font-semibold text-foreground">{n.title}</span>
                    {ctx.symbol && (
                      <span className="text-xs font-mono text-muted-foreground/80">{ctx.symbol}</span>
                    )}
                    {!n.read && <span className="w-2 h-2 rounded-full bg-cyan-500 flex-shrink-0" />}
                  </div>
                  <p className="text-sm text-muted-foreground">{n.message}</p>
                  <div className="flex items-center gap-3 mt-1">
                    {n.strategy_name && (
                      <span className="text-xs text-cyan-400/70 font-medium">{n.strategy_name}</span>
                    )}
                    <span className="text-xs text-muted-foreground/60">
                      {new Date(n.created_at).toLocaleString()}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0 mt-1">
                  {!n.read && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleMarkRead(n.id); }}
                      className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                    >
                      Mark read
                    </button>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDeleteOne(n.id); }}
                    className="text-xs text-muted-foreground/50 hover:text-red-400 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AccountPage() {
  const location = useLocation();
  const { data: session } = useSession();
  const section = location.pathname.split('/')[2] || 'portfolio';
  const user = session?.user;

  return (
    <PageTransition>
      <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
        {/* User identity bar */}
        <div className="flex items-center gap-4 pb-4 border-b border-border">
          <div className="w-14 h-14 rounded-full bg-primary flex items-center justify-center text-foreground text-xl font-bold flex-shrink-0">
            {user?.name
              ? user.name.split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase()
              : '?'}
          </div>
          <div>
            <p className="text-lg font-semibold text-foreground">{user?.name || 'User'}</p>
            <p className="text-sm text-muted-foreground">{user?.email || ''}</p>
          </div>
        </div>

        {section === 'portfolio' && <AccountPortfolio />}
        {section === 'billing' && <AccountBilling />}
        {section === 'notifications' && <AccountNotifications />}
      </div>
    </PageTransition>
  );
}

export default AccountPage;
